import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useInsurancePayment from "../hooks/useInsurancePayment";

const defaultAssistance = {
    issueType: "claim",
    claimAmount: "",
    incidentDate: "",
    description: "",
    preferredContact: "email",
    attachments: "",
};

const formatCurrency = (value) => {
    if (value === null || value === undefined || value === "") {
        return "—";
    }
    const amount = Number(value);
    if (!Number.isFinite(amount) || amount <= 0) {
        return "—";
    }
    return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 0,
    }).format(amount);
};

const calculateMonthlyPremium = (policy) => {
    if (!policy) {
        return 0;
    }
    const annual = Number(
        policy.premium ??
        policy.premium_amount ??
        policy.coverage_amount ??
        0
    );
    if (!Number.isFinite(annual) || annual <= 0) {
        return 0;
    }
    return Math.round((annual / 12) * 100) / 100;
};

const labelStyle = {
    display: "block",
    fontWeight: 600,
    color: "#1e293b",
    marginBottom: "6px",
};

const UserInsuranceAssistance = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [form, setForm] = useState(defaultAssistance);
    const [submitting, setSubmitting] = useState(false);
    const { startPayment, processingKey } = useInsurancePayment();

    const context = useMemo(() => {
        const company = location.state?.company;
        const policy = location.state?.policy;
        return {
            company,
            policy,
            hasContext: Boolean(company || policy),
        };
    }, [location.state]);

    const monthlyPremium = useMemo(() => calculateMonthlyPremium(context.policy), [context.policy]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleStartPayTrial = () => {
        if (!context.policy || !context.company) {
            toast.error("Select a policy to start payment.");
            return;
        }

        const mPremium = calculateMonthlyPremium(context.policy);
        if (!mPremium) {
            toast.error("Premium information is missing for this policy.");
            return;
        }

        startPayment({
            referenceId: `assistance-${context.policy.id || context.policy.number || "policy"}`,
            amount: mPremium,
            policyMeta: {
                policyId: context.policy?.id || null,
                policyNumber: context.policy?.number || context.policy?.policy_number || null,
                product: context.policy?.coverageType || "Insurance Plan",
                insurer: context.company?.company_name || context.company?.name || "Insurance Partner",
                companyId: context.company?.id || null,
                checkoutTitle: context.company?.company_name || "Insurance Payment",
                notes: "Monthly premium payment",
            },
            description: "Monthly premium initiated from assistance center",
        });
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!form.description.trim()) {
            toast.error("Please provide a short description so our team can assist you.");
            return;
        }

        setSubmitting(true);
        try {
            await new Promise((resolve) => setTimeout(resolve, 900));
            toast.success("Our insurance concierge will contact you within the next few hours.");
            navigate("/user-insurance");
        } catch (error) {
            toast.error("Something went wrong. Please try again or reach out to support.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section className="dc-haslayout dc-dbsectionspace">
            <div className="subbody" style={{ width: '100%', padding: '2% 4%', backgroundColor: '#f8fafc' }}>

                <div style={{ maxWidth: "1000px", margin: "0 auto" }}>

                    {/* Modern Header Section */}
                    <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                        <div className="card-body p-4 d-flex align-items-center justify-content-between flex-wrap gap-3">
                            <div>
                                <h4 className="mb-1 fw-bold text-dark">Insurance Support Center</h4>
                                <p className="text-muted mb-0 small">Get rapid concierge assistance for claims, coverage questions, or support.</p>
                            </div>
                            <button className="btn btn-light border d-flex align-items-center gap-2"
                                style={{ borderRadius: '8px' }}
                                onClick={() => navigate("/user-insurance")}>
                                <i className="ti-arrow-left"></i> Back to Marketplace
                            </button>
                        </div>
                    </div>

                    <div className="row g-4">
                        {/* Selected Context Sidebar */}
                        {context.hasContext && (
                            <div className="col-12 col-lg-4 order-lg-2">
                                <div className="card border-0 shadow-sm sticky-top" style={{ borderRadius: '16px', top: '24px' }}>
                                    <div className="card-header bg-white border-bottom p-4">
                                        <h6 className="mb-0 fw-bold d-flex align-items-center gap-2">
                                            <i className="ti-shield text-primary"></i> Selected Policy
                                        </h6>
                                    </div>
                                    <div className="card-body p-4">
                                        <div className="mb-4">
                                            <small className="text-muted uppercase fw-bold d-block mb-1" style={{ fontSize: '10px' }}>Carrier</small>
                                            <div className="fw-bold text-dark fs-5">{context.company?.company_name || context.company?.name || "Partner"}</div>
                                            <div className="small text-muted mt-1 d-flex flex-column gap-1">
                                                <span><i className="ti-email me-2"></i>{context.company?.email || 'N/A'}</span>
                                                <span><i className="ti-mobile me-2"></i>{context.company?.phone || 'N/A'}</span>
                                            </div>
                                        </div>

                                        <div className="mb-4 bg-light rounded-3 p-3 border">
                                            <small className="text-muted uppercase fw-bold d-block mb-1" style={{ fontSize: '10px' }}>Policy Details</small>
                                            <div className="fw-bold text-dark mb-1">{context.policy?.policy_number || context.policy?.number || 'Draft Policy'}</div>
                                            <div className="small text-muted mb-3">{context.policy?.coverage_type || context.policy?.coverageType || "Standard Coverage"}</div>

                                            <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                                                <span className="small text-muted">Monthly:</span>
                                                <span className="fw-bold text-primary">{monthlyPremium ? formatCurrency(monthlyPremium) : '—'}</span>
                                            </div>
                                        </div>

                                        <button className="btn btn-primary w-100 py-2 d-flex align-items-center justify-content-center gap-2"
                                            style={{ backgroundColor: 'var(--themecolor)', borderColor: 'var(--themecolor)', borderRadius: '10px' }}
                                            onClick={handleStartPayTrial}
                                            disabled={processingKey === `assistance-${context.policy?.id || context.policy?.number || "policy"}`}>
                                            {processingKey === `assistance-${context.policy?.id || context.policy?.number || "policy"}` ? '...' : (
                                                <><i className="ti-credit-card"></i> Pay Monthly Premium</>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Main Assistance Form */}
                        <div className={context.hasContext ? "col-12 col-lg-8" : "col-12"}>
                            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '16px' }}>
                                <div className="card-header bg-white border-bottom p-4">
                                    <h6 className="mb-0 fw-bold">Request Claim Assistance</h6>
                                </div>
                                <div className="card-body p-4">
                                    {!context.hasContext && (
                                        <div className="alert alert-warning border-0 rounded-3 mb-4 d-flex align-items-start gap-3" style={{ backgroundColor: '#fffbeb', color: '#92400e' }}>
                                            <i className="ti-info-alt fs-5 mt-1"></i>
                                            <p className="mb-0 small">You are creating a general support request. For faster service, go to your policies and select "Request Assistance" directly from a specific coverage plan.</p>
                                        </div>
                                    )}

                                    <form onSubmit={handleSubmit}>
                                        <div className="row g-4 mb-4">
                                            <div className="col-12 col-md-4">
                                                <label className="fw-bold text-dark-50 uppercase mb-2 d-block" style={{ fontSize: '11px' }}>Assistance Type</label>
                                                <select name="issueType" className="form-select py-2 px-3 border-light-subtle rounded-3" value={form.issueType} onChange={handleChange}>
                                                    <option value="claim">Claim Submission</option>
                                                    <option value="status">Claim Status Check</option>
                                                    <option value="documents">Document Guidance</option>
                                                    <option value="other">General Support</option>
                                                </select>
                                            </div>
                                            <div className="col-12 col-md-4">
                                                <label className="fw-bold text-dark-50 uppercase mb-2 d-block" style={{ fontSize: '11px' }}>Estimated Claim (₹)</label>
                                                <input type="number" name="claimAmount" className="form-control py-2 border-light-subtle rounded-3" placeholder="Ex: 50,000" value={form.claimAmount} onChange={handleChange} min="0" />
                                            </div>
                                            <div className="col-12 col-md-4">
                                                <label className="fw-bold text-dark-50 uppercase mb-2 d-block" style={{ fontSize: '11px' }}>Incident Date</label>
                                                <input type="date" name="incidentDate" className="form-control py-2 border-light-subtle rounded-3" value={form.incidentDate} onChange={handleChange} />
                                            </div>
                                        </div>

                                        <div className="mb-4">
                                            <label className="fw-bold text-dark-50 uppercase mb-2 d-block" style={{ fontSize: '11px' }}>Description of Incident *</label>
                                            <textarea name="description" className="form-control border-light-subtle rounded-3" rows="4" placeholder="Briefly explain what happened and what help you need..." value={form.description} onChange={handleChange} required></textarea>
                                        </div>

                                        <div className="row g-4 mb-5">
                                            <div className="col-12 col-md-6">
                                                <label className="fw-bold text-dark-50 uppercase mb-2 d-block" style={{ fontSize: '11px' }}>Preferred Contact</label>
                                                <select name="preferredContact" className="form-select py-2 border-light-subtle rounded-3" value={form.preferredContact} onChange={handleChange}>
                                                    <option value="email">Official Email</option>
                                                    <option value="phone">Direct Phone Call</option>
                                                    <option value="whatsapp">WhatsApp Business</option>
                                                </select>
                                            </div>
                                            <div className="col-12 col-md-6">
                                                <label className="fw-bold text-dark-50 uppercase mb-2 d-block" style={{ fontSize: '11px' }}>Evidence Link (Docs/Photos)</label>
                                                <input type="url" name="attachments" className="form-control py-2 border-light-subtle rounded-3" placeholder="https://drive.google.com/..." value={form.attachments} onChange={handleChange} />
                                            </div>
                                        </div>

                                        <div className="d-flex justify-content-end gap-3 pt-4 border-top">
                                            <button type="button" className="btn btn-light border px-4" style={{ borderRadius: '10px' }} onClick={() => navigate("/user-insurance")}>Cancel</button>
                                            <button type="submit" className="btn btn-primary px-5 fw-bold"
                                                style={{ backgroundColor: 'var(--themecolor)', borderColor: 'var(--themecolor)', borderRadius: '10px' }}
                                                disabled={submitting}>
                                                {submitting ? 'Sending Request...' : 'Submit Support Ticket'}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .uppercase { text-transform: uppercase; }
                .fw-bold { font-weight: 700; }
                .text-primary { color: var(--themecolor) !important; }
                .bg-light { background-color: #f8fafc !important; }
                .border-light-subtle { border-color: #e5e7eb !important; }
                @media (max-width: 991px) {
                    .order-lg-2 { order: -1 !important; margin-bottom: 24px; }
                    .sticky-top { position: static !important; }
                }
            `}</style>
        </section>
    );
};

export default UserInsuranceAssistance;

